import type { ProtocolImageDto } from "@gazelab/shared";

export interface PreloadedImage {
  imageId: string;
  sequenceIndex: number;
  kind: "image" | "video";
  /** URL `blob:` local; se revoca al terminar la sesión. */
  url: string;
  naturalWidth: number;
  naturalHeight: number;
  /** Solo video: duración según el navegador. */
  durationMs: number | null;
  /** Imagen: elemento decodificado que se mantiene vivo (caché de decodificación). */
  element: HTMLImageElement | null;
}

export interface PreloadProgress {
  done: number;
  total: number;
  /** Bytes descargados / totales (los videos pesan mucho más que las imágenes). */
  loadedBytes: number;
  totalBytes: number;
}

/**
 * Descarga (con token) TODOS los estímulos del protocolo antes de mostrarlos: las imágenes se
 * decodifican y los videos se cargan completos en memoria (Blob), así la reproducción nunca
 * depende de la red. Si uno falla, se lanza el error: nunca se presenta un estímulo no listo.
 */
export async function preloadImages(
  images: ReadonlyArray<ProtocolImageDto>,
  fetchBlob: (imageId: string, onBytes?: (loaded: number) => void) => Promise<Blob>,
  onProgress?: (p: PreloadProgress) => void,
): Promise<Map<string, PreloadedImage>> {
  const out = new Map<string, PreloadedImage>();
  const totalBytes = images.reduce((acc, i) => acc + (i.sizeBytes ?? 0), 0);
  let doneBytes = 0;
  let done = 0;
  const report = (current = 0) => onProgress?.({ done, total: images.length, loadedBytes: doneBytes + current, totalBytes });
  report();
  try {
    for (const meta of images) {
      const blob = await fetchBlob(meta.imageId, (loaded) => report(loaded));
      const url = URL.createObjectURL(blob);
      if (meta.kind === "video") {
        const info = await loadVideoMetadata(url);
        out.set(meta.imageId, { imageId: meta.imageId, sequenceIndex: meta.sequenceIndex, kind: "video", url, ...info, element: null });
      } else {
        const el = new Image();
        el.decoding = "async";
        el.src = url;
        await el.decode();
        out.set(meta.imageId, {
          imageId: meta.imageId,
          sequenceIndex: meta.sequenceIndex,
          kind: "image",
          url,
          naturalWidth: el.naturalWidth,
          naturalHeight: el.naturalHeight,
          durationMs: null,
          element: el,
        });
      }
      doneBytes += meta.sizeBytes ?? blob.size;
      done++;
      report();
    }
  } catch (e) {
    revokePreloaded(out);
    throw e;
  }
  return out;
}

/** Comprueba que el navegador puede decodificar el video y lee sus dimensiones y duración. */
function loadVideoMetadata(url: string): Promise<{ naturalWidth: number; naturalHeight: number; durationMs: number }> {
  return new Promise((resolve, reject) => {
    const v = document.createElement("video");
    v.preload = "auto";
    v.muted = true;
    const done = () => {
      v.removeAttribute("src");
      v.load();
    };
    v.onloadeddata = () => {
      const info = { naturalWidth: v.videoWidth, naturalHeight: v.videoHeight, durationMs: Math.round(v.duration * 1000) };
      done();
      if (!info.naturalWidth || !info.naturalHeight) reject(new Error("El navegador no pudo leer el video"));
      else resolve(info);
    };
    v.onerror = () => {
      done();
      reject(new Error("Este navegador no puede reproducir uno de los videos del estudio"));
    };
    v.src = url;
  });
}

export function revokePreloaded(images: Map<string, PreloadedImage>) {
  for (const img of images.values()) URL.revokeObjectURL(img.url);
  images.clear();
}

/**
 * Imagen neutra de práctica generada localmente (no es un estímulo del estudio y sus datos
 * nunca se envían).
 */
export async function createPracticeImage(width = 1600, height = 1200): Promise<{ url: string; naturalWidth: number; naturalHeight: number }> {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const g = canvas.getContext("2d");
  if (!g) throw new Error("Canvas no disponible");
  const grad = g.createLinearGradient(0, 0, width, height);
  grad.addColorStop(0, "#c9d6df");
  grad.addColorStop(1, "#e8e2d0");
  g.fillStyle = grad;
  g.fillRect(0, 0, width, height);
  const shapes: Array<[number, number, number, string]> = [
    [0.25, 0.3, 0.12, "#7c9eb2"],
    [0.7, 0.35, 0.09, "#b5838d"],
    [0.45, 0.72, 0.14, "#8d9b6a"],
  ];
  for (const [x, y, r, c] of shapes) {
    g.beginPath();
    g.arc(x * width, y * height, r * width, 0, Math.PI * 2);
    g.fillStyle = c;
    g.fill();
  }
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("No se pudo generar la imagen de práctica"))), "image/png"),
  );
  return { url: URL.createObjectURL(blob), naturalWidth: width, naturalHeight: height };
}
